import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, ButtonGroup, Card, CardActionArea, CardContent, Checkbox, CircularProgress, Collapse,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup, InputAdornment,
    Link as MuiLink, List, ListItem, ListItemText, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SchemaIcon from '@mui/icons-material/Schema';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MemoryIcon from '@mui/icons-material/Memory';
import FilterListIcon from '@mui/icons-material/FilterList';
import { listLibraryItems, requestLibraryDownloads } from './apiLibrary';
import { BRAND, FILE_KIND_LABELS, formatDate, startDownloads } from './libraryConstants';
import VerificationChip from './VerificationChip';

const PAGE_SIZE = 50;
const MIN_QUERY = 2;

const FILE_ICONS = {
    footprint_dwg: DesignServicesIcon,
    preview_image: ImageIcon,
    footprint_pdf: PictureAsPdfIcon,
    schematic_dwg: SchemaIcon,
    other: AttachFileIcon
};

const FileIcons = ({ files }) => {
    const kinds = [...new Set((files || []).map(f => f.kind))];
    return (
        <Stack direction="row" spacing={0.5}>
            {kinds.map(k => {
                const Icon = FILE_ICONS[k] || AttachFileIcon;
                return (
                    <Tooltip key={k} title={FILE_KIND_LABELS[k] || k}>
                        <Icon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                );
            })}
        </Stack>
    );
};

const Thumb = ({ item, size = 48 }) => (
    <Box sx={{
        width: size, height: size, borderRadius: 1, bgcolor: 'grey.100', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
    }}>
        {item.previewUrl
            ? <img src={item.previewUrl} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : <MemoryIcon sx={{ color: 'grey.400' }} />}
    </Box>
);

// Checkbox list with facet counts. options: [{ code, label }]
const FacetGroup = ({ title, options, counts, selected, onToggle }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.primary, mb: 0.5 }}>{title}</Typography>
        <FormGroup>
            {options.map(o => {
                const count = counts[o.code] || 0;
                const checked = selected.includes(o.code);
                return (
                    <FormControlLabel
                        key={o.code}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem', color: count || checked ? 'text.primary' : 'text.disabled' } }}
                        control={<Checkbox size="small" checked={checked} onChange={() => onToggle(o.code)} />}
                        label={`${o.label} (${o.code}) · ${count}`}
                    />
                );
            })}
        </FormGroup>
    </Box>
);

const LibrarySearch = ({ groupId, config, filters, refreshKey, onFiltersChange, onOpenItem, onAddDevice }) => {
    const theme = useTheme();
    const stacked = useMediaQuery(theme.breakpoints.down('md'));
    const compact = useMediaQuery(theme.breakpoints.down('sm'));

    const [data, setData] = useState({ items: [], total: 0, facets: { deviceTypes: [], manufacturers: [] } });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [qInput, setQInput] = useState(filters.q || '');
    const [selected, setSelected] = useState({}); // itemId -> code
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadResult, setDownloadResult] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    // Keep the box in step when filters change from outside (Clear filters, back button)
    useEffect(() => { setQInput(filters.q || ''); }, [filters.q]);

    // Debounced search term -> URL
    useEffect(() => {
        const next = qInput.trim().length >= MIN_QUERY ? qInput.trim() : '';
        if (next === (filters.q || '')) return undefined;
        const t = setTimeout(() => onFiltersChange({ q: next, page: 1 }), 300);
        return () => clearTimeout(t);
    }, [qInput]); // eslint-disable-line react-hooks/exhaustive-deps

    const typeKey = filters.type.join(',');
    const mfrKey = filters.mfr.join(',');
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        listLibraryItems(groupId, {
            type: filters.type, mfr: filters.mfr, q: filters.q, sort: filters.sort,
            page: filters.page, limit: PAGE_SIZE
        }).then(res => {
            if (cancelled) return;
            setLoading(false);
            if (res.error) { setError(res.error); return; }
            setError('');
            setData(res);
        });
        return () => { cancelled = true; };
    }, [groupId, typeKey, mfrKey, filters.q, filters.sort, filters.page, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const countMap = (rows) => Object.fromEntries((rows || []).map(r => [r.code, r.count]));
    const typeCounts = useMemo(() => countMap(data.facets.deviceTypes), [data.facets]);
    const mfrCounts = useMemo(() => countMap(data.facets.manufacturers), [data.facets]);

    const toggle = (key, code) => {
        const list = filters[key];
        onFiltersChange({ [key]: list.includes(code) ? list.filter(c => c !== code) : [...list, code], page: 1 });
    };
    const activeFilterCount = filters.type.length + filters.mfr.length;
    const hasFilters = activeFilterCount || filters.q;
    const clearFilters = () => { setQInput(''); onFiltersChange({ type: [], mfr: [], q: '', page: 1 }); };

    const selectedIds = Object.keys(selected);
    const pageIds = data.items.map(i => i._id);
    const allOnPage = pageIds.length > 0 && pageIds.every(id => selected[id]);
    const toggleRow = (item) => setSelected(s => {
        const next = { ...s };
        if (next[item._id]) delete next[item._id]; else next[item._id] = item.code;
        return next;
    });
    const togglePage = () => setSelected(s => {
        const next = { ...s };
        if (allOnPage) pageIds.forEach(id => delete next[id]);
        else data.items.forEach(i => { next[i._id] = i.code; });
        return next;
    });

    const download = async (kinds) => {
        setMenuAnchor(null);
        setDownloading(true);
        const res = await requestLibraryDownloads(groupId, { itemIds: selectedIds, kinds });
        setDownloading(false);
        if (res.error) { setError(res.error); return; }
        if (!res.files.length) {
            setDownloadResult({ ...res, empty: true });
            return;
        }
        await startDownloads(res.files);
        // Several files: browsers may block all but the first, so offer the links too
        if (res.files.length > 1 || res.unavailable.length) setDownloadResult(res);
    };

    const filterPanel = (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <FacetGroup
                title="Device type"
                options={config.taxonomy.deviceTypes}
                counts={typeCounts}
                selected={filters.type}
                onToggle={(c) => toggle('type', c)}
            />
            <FacetGroup
                title="Manufacturer"
                options={config.taxonomy.manufacturers}
                counts={mfrCounts}
                selected={filters.mfr}
                onToggle={(c) => toggle('mfr', c)}
            />
            <Button size="small" onClick={clearFilters} disabled={!hasFilters}>Clear filters</Button>
        </Paper>
    );

    const results = compact ? (
        <Stack spacing={1}>
            {data.items.map(item => (
                <Card key={item._id} variant="outlined" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={Boolean(selected[item._id])} onChange={() => toggleRow(item)} inputProps={{ 'aria-label': `Select ${item.code}` }} />
                    <CardActionArea onClick={() => onOpenItem(item._id)} sx={{ minWidth: 0 }}>
                        <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1.5, pl: 0, '&:last-child': { pb: 1.5 } }}>
                            <Thumb item={item} size={44} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>{item.code}</Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>{item.title || item.description}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <Typography variant="caption">r{item.revision}</Typography>
                                    <FileIcons files={item.files} />
                                </Stack>
                            </Box>
                        </CardContent>
                    </CardActionArea>
                </Card>
            ))}
        </Stack>
    ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, color: BRAND.primary } }}>
                        <TableCell padding="checkbox">
                            <Checkbox
                                checked={allOnPage}
                                indeterminate={!allOnPage && pageIds.some(id => selected[id])}
                                onChange={togglePage}
                                inputProps={{ 'aria-label': 'Select all on this page' }}
                            />
                        </TableCell>
                        <TableCell />
                        <TableCell>Device</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Rev</TableCell>
                        <TableCell>Files</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map(item => (
                        <TableRow key={item._id} hover selected={Boolean(selected[item._id])} sx={{ cursor: 'pointer' }} onClick={() => onOpenItem(item._id)}>
                            <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                <Checkbox checked={Boolean(selected[item._id])} onChange={() => toggleRow(item)} inputProps={{ 'aria-label': `Select ${item.code}` }} />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}><Thumb item={item} /></TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.code}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {item.manufacturer} · {item.model}{item.variant ? ` (${item.variant})` : ''}
                                    {item.publishedAt ? ` · ${formatDate(item.publishedAt)}` : ''}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>
                                <Typography variant="body2" noWrap title={item.title || item.description} sx={{ mb: 0.5 }}>{item.title || item.description}</Typography>
                                <VerificationChip verification={item.verification} />
                            </TableCell>
                            <TableCell>r{item.revision}</TableCell>
                            <TableCell><FileIcons files={item.files} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: stacked ? 'column' : 'row', gap: 3, alignItems: 'flex-start' }}>
                {!stacked && <Box sx={{ width: 240, flexShrink: 0 }}>{filterPanel}</Box>}

                <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: stacked ? 0 : 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            value={qInput}
                            onChange={e => setQInput(e.target.value)}
                            placeholder="Search code, model, part number or description"
                            inputProps={{ 'aria-label': 'Search the device library', maxLength: 200 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                            helperText={qInput.trim().length === 1 ? `Type at least ${MIN_QUERY} characters` : ' '}
                        />
                        <TextField
                            select
                            size="small"
                            label="Sort"
                            value={filters.sort}
                            onChange={e => onFiltersChange({ sort: e.target.value, page: 1 })}
                            sx={{ minWidth: 190 }}
                        >
                            <MenuItem value="code">Code</MenuItem>
                            <MenuItem value="-publishedAt">Recently published</MenuItem>
                        </TextField>
                    </Stack>

                    {/* Narrow screens: filters fold away so results stay above the fold */}
                    {stacked && (
                        <Box sx={{ mb: 2 }}>
                            <Button
                                size="small"
                                startIcon={<FilterListIcon />}
                                onClick={() => setShowFilters(s => !s)}
                                aria-expanded={showFilters}
                            >
                                Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
                            </Button>
                            <Collapse in={showFilters}><Box sx={{ mt: 1 }}>{filterPanel}</Box></Collapse>
                        </Box>
                    )}

                    {selectedIds.length > 0 && (
                        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: '#FFF4EC', borderRadius: 2 }} elevation={0}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedIds.length} selected</Typography>
                            <ButtonGroup variant="contained" disabled={downloading} sx={{ '& .MuiButton-root': { bgcolor: BRAND.action, '&:hover': { bgcolor: BRAND.actionHover } } }}>
                                <Button startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} onClick={() => download(['footprint_dwg'])}>
                                    Download footprints ({selectedIds.length})
                                </Button>
                                <Button size="small" aria-label="Other file types" onClick={e => setMenuAnchor(e.currentTarget)}>
                                    <ArrowDropDownIcon />
                                </Button>
                            </ButtonGroup>
                            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                                {Object.keys(config.taxonomy.fileKinds).filter(k => k !== 'footprint_dwg').map(k => (
                                    <MenuItem key={k} onClick={() => download([k])}>{FILE_KIND_LABELS[k] || k}</MenuItem>
                                ))}
                                <Divider />
                                <MenuItem onClick={() => download(Object.keys(config.taxonomy.fileKinds))}>All file types</MenuItem>
                            </Menu>
                            <Button size="small" onClick={() => setSelected({})}>Clear selection</Button>
                        </Paper>
                    )}

                    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                    {loading && !data.items.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : data.items.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
                            <MemoryIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                            <Typography variant="h6" sx={{ mt: 1 }}>No devices match.</Typography>
                            <Typography color="text.secondary">{hasFilters ? 'Try fewer filters.' : 'Nothing has been published yet.'}</Typography>
                            {onAddDevice && (
                                <Button variant="outlined" sx={{ mt: 2 }} onClick={onAddDevice}>Add a device</Button>
                            )}
                        </Paper>
                    ) : (
                        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                            {results}
                            {data.total > PAGE_SIZE && (
                                <TablePagination
                                    component="div"
                                    count={data.total}
                                    page={filters.page - 1}
                                    rowsPerPage={PAGE_SIZE}
                                    rowsPerPageOptions={[PAGE_SIZE]}
                                    onPageChange={(e, p) => onFiltersChange({ page: p + 1 })}
                                />
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {data.total} device{data.total === 1 ? '' : 's'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Dialog open={Boolean(downloadResult)} onClose={() => setDownloadResult(null)} fullWidth maxWidth="sm">
                <DialogTitle>{downloadResult && downloadResult.empty ? 'Nothing to download' : 'Your downloads'}</DialogTitle>
                <DialogContent>
                    {downloadResult && downloadResult.empty ? (
                        <Typography>None of the selected devices have that file type.</Typography>
                    ) : downloadResult && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                If your browser asked about multiple downloads or only saved some files, use these links. They work for 15 minutes.
                            </Typography>
                            <List dense>
                                {downloadResult.files.map(f => (
                                    <ListItem key={f.url} disableGutters>
                                        <ListItemText primary={<MuiLink href={f.url} download={f.fileName}>{f.fileName}</MuiLink>} secondary={FILE_KIND_LABELS[f.kind]} />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                    {downloadResult && downloadResult.unavailable && downloadResult.unavailable.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {downloadResult.unavailable.length} selected device{downloadResult.unavailable.length === 1 ? ' is' : 's are'} no longer available
                            ({downloadResult.unavailable.map(id => selected[id] || id).join(', ')}).
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setDownloadResult(null)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default LibrarySearch;
